import { loadMenuImages } from '@theme/seal-menu-images';

class SealMobileMenu extends HTMLElement {
  static observedAttributes = ['hidden'];
  #controller;
  #observer;
  #drawer;
  #details;
  #back;
  #views;
  #active;
  #stack = [];
  #animations = [];
  #sequence = 0;
  #handledEscape = false;
  #mobile = window.matchMedia('(max-width: 749px)');
  #reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    queueMicrotask(() => {
      if (this.isConnected && !this.#controller) this.#initialize();
    });
  }

  attributeChangedCallback(name, previous, value) {
    if (value === null || !this.isConnected || !this.#controller) return;
    queueMicrotask(() => {
      if (!this.isConnected || !this.hidden) return;
      this.#controller.abort();
      this.#observer?.disconnect();
      this.#initialize();
    });
  }

  #initialize() {
    this.#drawer = this.closest('.menu-drawer');
    this.#details = this.closest('.menu-drawer-container');
    if (!this.#drawer || !this.#details) return;

    this.#views = [...this.querySelectorAll('[data-seal-view]')];
    this.#active = this.#views[0];
    this.#back = this.#drawer.querySelector('.seal-mobile-menu-back');
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#drawer.addEventListener('click', this.#click, { signal });
    this.addEventListener('input', this.#filterCountries, { signal });
    this.addEventListener('submit', this.#localize, { signal });
    this.#back.addEventListener('click', this.#goBack, { signal });
    this.#drawer.addEventListener('keydown', this.#keydown, { signal });
    this.#drawer.addEventListener('keyup', this.#keyup, { signal });
    this.#mobile.addEventListener('change', this.#reset, { signal });
    this.#observer = new MutationObserver(() => {
      this.hidden = false;
      this.#drawer.dataset.sealPanels = '';
      if (!this.#details.open || this.#details.dataset.sealOverlayState === 'closed') this.#reset();
      else this.#loadActiveImages();
    });
    this.#observer.observe(this.#details, { attributes: true, attributeFilter: ['open', 'data-seal-overlay-state'] });
    this.hidden = false;
    this.#drawer.dataset.sealPanels = '';
    this.#reset();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = undefined;
    this.#observer?.disconnect();
    this.#animations.forEach(animation => animation.cancel());
    if (this.#drawer) {
      delete this.#drawer.dataset.sealPanels;
      delete this.#drawer.dataset.sealSubview;
      this.#drawer.querySelector('.menu-drawer__utility-links')?.removeAttribute('inert');
    }
  }

  #click = event => {
    if (!this.#mobile.matches || !(event.target instanceof Element)) return;
    const link = event.target.closest('a[href]');
    if (
      link instanceof HTMLAnchorElement &&
      !event.defaultPrevented && event.button === 0 &&
      !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey &&
      !link.hasAttribute('download') && (!link.target || link.target.toLowerCase() === '_self') &&
      link.hash && link.origin === location.origin &&
      link.pathname === location.pathname && link.search === location.search
    ) {
      this.closest('header-drawer')?.close();
      return;
    }
    const trigger = event.target.closest('[data-seal-view-target]');
    if (!trigger) return;
    const next = this.#views.find(view => view.dataset.sealView === trigger.dataset.sealViewTarget);
    if (!next) return;
    this.#stack.push({ view: this.#active, trigger, scroll: this.#active.scrollTop });
    trigger.setAttribute('aria-expanded', 'true');
    this.#show(next, false);
  };

  #goBack = () => {
    const previous = this.#stack.pop();
    if (!previous) return;
    previous.trigger.setAttribute('aria-expanded', 'false');
    this.#show(previous.view, true, previous);
  };

  #filterCountries = event => {
    const search = event.target.closest('[data-seal-country-search]');
    if (!search) return;
    const normalize = value => value.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLocaleLowerCase().trim();
    const query = normalize(search.value);
    const view = search.closest('[data-seal-view]');
    const countries = [...view.querySelectorAll('[data-seal-country]')];
    let count = 0;
    countries.forEach(country => {
      country.hidden = !normalize(country.dataset.sealCountry).includes(query);
      if (!country.hidden) count++;
    });
    view.querySelector('[data-seal-no-countries]').hidden = count > 0;
    view.querySelector('[data-seal-country-count]').textContent = search.dataset.resultsLabel.replace('[count]', count);
  };

  #localize = event => {
    if (!event.target.matches('.seal-mobile-localization__form')) return;
    const returnTo = event.target.querySelector('[name="return_to"]');
    if (!returnTo) return;
    const destination = new URL(window.location.href);
    destination.searchParams.delete('section_id');
    returnTo.value = destination.pathname + destination.search + destination.hash;
  };

  #show(next, back, previous) {
    const sequence = ++this.#sequence;
    this.#animations.forEach(animation => animation.cancel());
    const outgoing = this.#active;
    this.#views.forEach(view => {
      view.hidden = view !== outgoing && view !== next;
      view.inert = view !== next;
    });
    next.hidden = false;
    next.scrollTop = previous?.scroll ?? 0;
    this.#active = next;
    this.#loadActiveImages();
    this.#updateChrome();
    (previous?.trigger ?? next.querySelector('[data-seal-view-focus]'))?.focus({ preventScroll: true });

    if (this.#reduced.matches) {
      outgoing.hidden = true;
      return;
    }
    const direction = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
    const options = { duration: 240, easing: 'cubic-bezier(.22, 1, .36, 1)', fill: 'both' };
    this.#animations = [
      outgoing.animate([
        { transform: 'translateX(0)', opacity: 1 },
        { transform: `translateX(${(back ? 20 : -16) * direction}%)`, opacity: 0 }
      ], options),
      next.animate([
        { transform: `translateX(${(back ? -16 : 24) * direction}%)`, opacity: 0 },
        { transform: 'translateX(0)', opacity: 1 }
      ], options)
    ];
    Promise.allSettled(this.#animations.map(animation => animation.finished)).then(() => {
      if (sequence !== this.#sequence) return;
      outgoing.hidden = true;
      this.#animations.forEach(animation => animation.cancel());
      this.#animations = [];
    });
  }

  #loadActiveImages() {
    if (this.#mobile.matches && this.#details.open && this.#details.dataset.sealOverlayState !== 'closed') {
      loadMenuImages(this.#active);
    }
  }

  #updateChrome() {
    const nested = this.#stack.length > 0;
    this.#back.hidden = !nested;
    this.#drawer.toggleAttribute('data-seal-subview', nested);
    const utilities = this.#drawer.querySelector('.menu-drawer__utility-links');
    if (utilities) utilities.inert = nested && this.#mobile.matches;
  }

  #reset = () => {
    ++this.#sequence;
    this.#animations.forEach(animation => animation.cancel());
    this.#animations = [];
    this.#stack = [];
    this.#active = this.#views[0];
    this.#views.forEach(view => {
      view.hidden = view !== this.#active;
      view.inert = view !== this.#active;
      view.scrollTop = 0;
    });
    this.#drawer.querySelectorAll('[data-seal-view-target]').forEach(button => button.setAttribute('aria-expanded', 'false'));
    this.querySelectorAll('accordion-custom details').forEach(details => { details.open = false; });
    const search = this.querySelector('[data-seal-country-search]');
    if (search) {
      search.value = '';
      this.querySelectorAll('[data-seal-country]').forEach(country => { country.hidden = false; });
      this.querySelector('[data-seal-no-countries]').hidden = true;
      this.querySelector('[data-seal-country-count]').textContent = '';
    }
    this.#updateChrome();
    this.#loadActiveImages();
  };

  #keydown = event => {
    if (!this.#mobile.matches) return;
    if (event.target.closest('.menu-drawer__submenu')) return;
    if (event.key === 'Escape' && this.#stack.length) {
      event.preventDefault();
      event.stopPropagation();
      this.#handledEscape = true;
      this.#goBack();
    }
    if (event.key !== 'Tab') return;
    const focusable = [...this.#drawer.querySelectorAll('button:not(:disabled), a[href], summary, input, select, [tabindex="0"]')]
      .filter(element => {
        const closedDetails = element.closest('details:not([open])');
        const inClosedContent = closedDetails && !closedDetails.querySelector(':scope > summary')?.contains(element);
        return !inClosedContent && !element.closest('[inert]') && element.getClientRects().length > 0 && getComputedStyle(element).visibility !== 'hidden';
      });
    const first = focusable[0];
    const last = focusable.at(-1);
    if (event.shiftKey && (document.activeElement === first || !focusable.includes(document.activeElement))) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && (document.activeElement === last || !focusable.includes(document.activeElement))) {
      event.preventDefault();
      first?.focus();
    }
  };

  #keyup = event => {
    if (event.key === 'Escape' && this.#handledEscape) {
      event.stopPropagation();
      this.#handledEscape = false;
    }
  };
}

if (!customElements.get('seal-mobile-menu')) customElements.define('seal-mobile-menu', SealMobileMenu);
